import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const Title = styled.h1`
  color: #FECE1B;
  margin-bottom: 2rem;
  font-size: 2.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;

  h2 {
    color: #1a1a1a;
    margin-bottom: 1rem;
    font-size: 1.5rem;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  h3 {
    color: #333;
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;
    font-size: 1.25rem;

    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }

  p {
    margin-bottom: 1rem;
    color: #555;
  }

  ul, ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      color: #555;
    }
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;

  h3 {
    margin-top: 0;
    color: #FECE1B;
  }

  p {
    margin-bottom: 0.5rem;
  }
`;

const PrivacyPolicyPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content="Privacy policy for ZAN RESIN SURFACES LTD - How we collect, use, and protect your personal information." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/privacy-policy/" />
      </Helmet>

      <PageContainer>
        <Title>Privacy Policy</Title>

        <Section>
          <h2>1. Introduction</h2>
          <p>
            ZAN RESIN SURFACES LTD ("we", "our", or "us") is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
          </p>
          <p>
            <strong>Company Details:</strong><br />
            ZAN RESIN SURFACES LTD<br />
            Company Number: 16698534<br />
            Registered Office: Broadfield Broadhurst Gardens, London, England, NW6 3BN<br />
            Email: info@resinsurfaceslondon.co.uk
          </p>
        </Section>

        <Section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>We may collect the following types of personal information:</p>
          <ul>
            <li>Name and contact details (email, phone number, address)</li>
            <li>Property address for service delivery</li>
            <li>Project requirements and specifications</li>
            <li>Payment information (processed securely through third-party providers)</li>
            <li>Communication preferences</li>
          </ul>

          <h3>2.2 Technical Information</h3>
          <p>We may automatically collect:</p>
          <ul>
            <li>IP address and browser information</li>
            <li>Website usage data and analytics</li>
            <li>Device information and operating system</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </Section>

        <Section>
          <h2>3. How We Use Your Information</h2>
          <p>We use your personal information for the following purposes:</p>
          <ul>
            <li>Providing and delivering our resin surfacing services</li>
            <li>Processing quotations and bookings</li>
            <li>Communicating about your project and service updates</li>
            <li>Processing payments and invoicing</li>
            <li>Improving our website and services</li>
            <li>Marketing communications (with your consent)</li>
            <li>Legal compliance and record keeping</li>
          </ul>
        </Section>

        <Section>
          <h2>4. Legal Basis for Processing</h2>
          <p>Under GDPR, we process your personal data based on:</p>
          <ul>
            <li><strong>Contract:</strong> To fulfill our service agreement with you</li>
            <li><strong>Legitimate Interest:</strong> For business operations and service improvement</li>
            <li><strong>Consent:</strong> For marketing communications and cookies</li>
            <li><strong>Legal Obligation:</strong> For tax, accounting, and regulatory compliance</li>
          </ul>
        </Section>

        <Section>
          <h2>5. Information Sharing and Disclosure</h2>
          <p>We may share your information with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Payment processors, delivery companies, and contractors</li>
            <li><strong>Business Partners:</strong> Material suppliers and subcontractors (only as necessary)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale</li>
          </ul>
          <p>We do not sell, rent, or trade your personal information to third parties for marketing purposes.</p>
        </Section>

        <Section>
          <h2>6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul>
            <li>Encryption of sensitive data in transit and at rest</li>
            <li>Regular security assessments and updates</li>
            <li>Access controls and staff training</li>
            <li>Secure data storage and backup procedures</li>
          </ul>
        </Section>

        <Section>
          <h2>7. Data Retention</h2>
          <p>
            We retain your personal information only as long as necessary for the purposes outlined in this policy, including:
          </p>
          <ul>
            <li>Customer records: 7 years (for tax and accounting purposes)</li>
            <li>Project documentation: 10 years (for warranty purposes)</li>
            <li>Marketing data: Until you withdraw consent</li>
            <li>Website analytics: 26 months (Google Analytics default)</li>
          </ul>
        </Section>

        <Section>
          <h2>8. Your Rights</h2>
          <p>Under GDPR, you have the following rights regarding your personal data:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
            <li><strong>Erasure:</strong> Request deletion of your data</li>
            <li><strong>Restriction:</strong> Limit how we process your data</li>
            <li><strong>Portability:</strong> Receive your data in a structured format</li>
            <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing communications</li>
          </ul>
          <p>
            To exercise these rights, contact us at info@resinsurfaceslondon.co.uk. We will respond within one month.
          </p>
        </Section>

        <Section>
          <h2>9. Cookies and Tracking</h2>
          <p>
            Our website uses cookies and similar technologies to enhance your browsing experience. We use:
          </p>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for website functionality</li>
            <li><strong>Analytics Cookies:</strong> Google Analytics for website performance</li>
            <li><strong>Marketing Cookies:</strong> For targeted advertising (with consent)</li>
          </ul>
          <p>
            You can control cookie preferences through your browser settings or our cookie consent banner.
          </p>
        </Section>

        <Section>
          <h2>10. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
          </p>
        </Section>

        <Section>
          <h2>11. Children's Privacy</h2>
          <p>
            Our services are not directed to children under 16. We do not knowingly collect personal information from children under 16. If we become aware of such collection, we will delete the information immediately.
          </p>
        </Section>

        <Section>
          <h2>12. International Transfers</h2>
          <p>
            Some of our service providers may be located outside the UK/EEA. When we transfer data internationally, we ensure appropriate safeguards are in place to protect your information.
          </p>
        </Section>

        <Section>
          <h2>13. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on our website and updating the "Last Updated" date.
          </p>
        </Section>

        <ContactInfo>
          <h3>Contact Us</h3>
          <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
          <p><strong>ZAN RESIN SURFACES LTD</strong></p>
          <p>Company Number: 16698534</p>
          <p>Registered Office: Broadfield Broadhurst Gardens, London, England, NW6 3BN</p>
          <p>Email: <a href="mailto:info@resinsurfaceslondon.co.uk">info@resinsurfaceslondon.co.uk</a></p>
          <p>Phone: <a href="tel:+447535935779">07535 935 779</a></p>
          <p><em>Last updated: January 2025</em></p>
        </ContactInfo>
      </PageContainer>
    </>
  );
};

export default PrivacyPolicyPage;
